<!--
  A convenient editor for all inputs as a single textfile

  Props:
    inputs: the array of all inputs
    entities: the list of defined entities
    synonymsOptions: the list of available synonyms,

  Emits:
    save(inputs): the new inputs
    close: to close the editor
-->

<template>
  <el-form class="inputs-text-editor"
    ref="form"
    :model="model"
    label-width="auto"
    :hide-required-asterisk="true">
    <el-row>
      <!-- a radio to select if synonyms should be displayed
        by the synonym's name, or by the text in the input -->
      <el-col :span="8">
        <el-form-item label="Synonyms identifier" label-width="135px">
          <!-- <el-radio-group :value="useSynonymName"
            @input="val => this.handleReset('useSynonymName', val)">
            <el-radio :label="true">Name</el-radio>
            <el-radio :label="false">Text</el-radio>
          </el-radio-group> -->
          <el-select :value="useSynonymName"
            @input="val => this.handleReset('useSynonymName', val)">
            <el-option
              key="name"
              label="Name"
              :value="true">
            </el-option>
            <el-option
              key="text"
              label="Text"
              :value="false">
            </el-option>
          </el-select>
        </el-form-item>
      </el-col>
      <el-col :span="5">
        <el-form-item label="Show priority" label-width="110px">
          <el-checkbox :value="usePriority"
            @input="val => this.handleReset('usePriority', val)">
          </el-checkbox>
        </el-form-item>
      </el-col>
      <!-- the format of the synonyms and entities -->
      <el-col :span="11">
        <el-form-item label="Inputs format" label-width="105px">
          <el-select :value="format" value-key="name"
            @input="val => this.handleReset('format', val)">
            <el-option v-for="format in FORMATS"
              :key="format.name"
              :label="format.name"
              :value="format">
            </el-option>
          </el-select>
        </el-form-item>
      </el-col>
      <!-- the main textarea to edit the inputs -->
      <el-col :span="24" style="margin-bottom: 20px;">
        <el-form-small-item
          prop="inputs"
          :rules="inputsValidator">
          <el-input type="textarea" :rows="20"
            v-model="model.inputs"></el-input>
        </el-form-small-item>
      </el-col>
      <!-- editor for the synonyms -->
      <el-col :span="24" style="margin-bottom: 20px; max-width: 800px;">
        <el-form-small-item
          v-for="(synonym, index) in model.synonyms"
          :key="synonym.name"
          required
          :prop="'synonyms.' + index + '.value'">
          <el-button-group style="width: 100%">
            <!-- a button to show problems -->
            <el-tooltip v-if="!synonym.value"
              placement="right" effect="light" :tabindex="-1">
              <template slot="content" v-if="synonym.inconsistent">
                Several items with the same <em>text</em> have different <em>synonyms</em>
              </template>
              <template slot="content" v-else>
                <em>Synonym</em> required
              </template>
              <el-button disaled
                :type="synonym.inconsistent ? 'warning' : 'info'"
                :icon="synonym.inconsistent ? 'el-icon-warning-outline' : 'el-icon-circle-plus-outline'"
                :tabindex="-1">
              </el-button>
            </el-tooltip>
            <el-button v-else disaled
              type="info"
              icon="el-icon-circle-check"
              :tabindex="-1">
            </el-button>
            <!-- the name (or text) of the synonym -->
            <el-input disabled
              :value="synonym.name">
            </el-input>
            <!-- the synonym it should use -->
            <el-filterable-entity-select
              :value="synonym.value"
              :disabled="synonym.disabled"
              @input="(val) => handleSynonym(synonym.name, val)"
              :options="synonymsOptions"
              type="synonym"
              value-field="synonym"
              label-field="name">
              <template slot="info" slot-scope="{item}">{{
                item && !item.local ? '(global)' : null
              }}</template>
            </el-filterable-entity-select>
          </el-button-group>
        </el-form-small-item>
      </el-col>
      <!-- Cancel / Reset / Validate buttons -->
      <el-col :span="24" style="text-align: center">
        <el-button-group style="justify-content: center;">
          <el-button @click="handleCancel" type="danger">Cancel</el-button>
          <el-button @click="handleReset" type="warning">Reset</el-button>
          <el-button @click="handleSave" type="primary">Validate</el-button>
        </el-button-group>
      </el-col>
    </el-row>
  </el-form>
</template>

<script>
import ElFormSmallItem from '../element/ElFormSmallItem.vue'
import ElFilterableEntitySelect from '../element/ElFilterableEntitySelect.vue'

const FORMATS = [{
  name: '#synonym @entity',
  // name: 'pri \uFF3F #synonym @entity',
  pattern: /([^@#]+)|@([^@#\s!%',.?-]+)|#([^@#\s!%',.?-]+)|./g,
  synonym: name => '#' + name,
  entity: name => '@' + name,
}, {
  name: '<synonym> @entity',
  // name: 'pri \uFF3F <synonym> @entity',
  pattern: /([^<>@]+)|@([^<>@\s!%',.?-]+)|<([^<>@]+)>|./g,
  synonym: name => '<' + name + '>',
  entity: name => '@' + name,
}]

export default {
  name: 'InputsTextEditor',
  components: {
    ElFormSmallItem,
    ElFilterableEntitySelect,
  },
  props: {
    inputs: Array,
    levels: Object,
    entities: Array,
    synonymsOptions: Array,
  },
  computed: {
    /* the synonym by synonymid */
    synonymsDict () {
      let out = {}
      for (let synonym of this.synonymsOptions) {
        out[synonym.synonym] = synonym
      }
      return out
    },
    /* a sorted array of synonyms to be displayed */
    synonymsList () {
      let out = []
      for (let name in this.synonymsRef) {
        let value = this.synonymsRef[name]
        if (value.used) out.push(value)
      }
      out.sort((a, b) => (a.name > b.name) - (a.name < b.name))
      return out
    },
    /* the entityid for each entity name */
    entitiesRef () {
      let out = {}
      for (let entity of this.entities) {
        out[entity.name] = entity.entity
      }
      return out
    },
    /* the text to display in the textarea */
    inputsText () { return this.inputsLines.join('\n') + '\n' },
  },
  data () {
    let self = this
    return {
      model: { // the model for the form
        inputs: undefined, // the textarea content
        synonyms: [], // the array of synonyms for the editor
      },
      inputsLines: [], // textarea as a list of lines
      synonymsRef: {}, // the synonym object for each name / text
      useSynonymName: true, // if should use name or text
      usePriority: true, // if should display and edit priority
      outputs: null, // the array of inputs to save
      errors: [], // the errors to display
      FORMATS: FORMATS, // the available formats
      format: FORMATS[1], // the chosen format
      inputsValidator: { // the validator for the textarea
        validator (rule, value, callback) {
          callback(
            self.errors.length ? new Error(self.errors[0]) : undefined
          )
        },
        trigger: 'blur',
      },
    }
  },
  /* init the model */
  created () { this.buildModel() },
  watch: {
    // update model.inputs from inputsLines
    inputsLines (inputs) {
      this.model.inputs = inputs.join('\n') + '\n'
    },
    // update model.synonyms from synonymsList
    synonymsList (synonyms) {
      this.model.synonyms = synonyms
    },
    // inputs () { this.buildModel() },
    // useSynonymName () { this.buildModel() },
    // format () { this.buildModel() },
    /* update the ouput when the model changed */
    model: {
      handler () { this.inputsChanged() },
      deep: true,
    },
  },
  methods: {
    /* build inputsLines and synonymsRef from inputs */
    buildModel () {
      const reg = /([^#@]+)|([#@])([^#@]*):(?:([0-9a-zA-Z_]+)|null)?/g
      let lines = [], synonyms = {}, levels = this.levels || {}
      // useSynonymName, init synonymsRef with all synonyms names
      if (this.useSynonymName) {
        for (let ref in this.synonymsDict) {
          let synonym = this.synonymsDict[ref]
          if (synonyms[synonym.name] && !synonym.local) continue
          synonyms[synonym.name] = {
            name: synonym.name,
            value: ref,
            inconsistent: false,
            disabled: true,
            used: false,
            local: synonym.local,
          }
        }
      }
      // parse all input, extract the synonyms and format the lines
      for (let [index, input] of this.inputs.entries()) {
        input = input.replace(reg, (_, text, prefix, stext, ref) => {
          if (text) return text
          else if (prefix === '@') return this.format.entity(stext)
          else {
            ref = ref || null
            if (this.useSynonymName) {
              stext = this.synonymsDict[ref].name || stext
            }
            let value = synonyms[stext]
            if (value) {
              value.used = true
              if (value.value !== ref) {
                value.value = null
                value.inconsistent = true
                value.disabled = false
              }
            } else {
              synonyms[stext] = {
                name: stext,
                value: ref,
                inconsistent: false,
                disabled: false,
                used: true,
              }
            }
            return this.format.synonym(stext)
          }
        })
        if (this.usePriority) {
          let level = (levels[index + ''] || 0) + 1
          lines.push(level + '\t' + input)
        } else {
          lines.push(input)
        }
      }
      this.inputsLines = lines
      this.synonymsRef = synonyms
    },
    /* update the ouput and synonymsRef */
    inputsChanged () {
      // const reg = /([^<>@]+)|@([^<>@\s!%',.?-]+)|<([^<>@]+)>|./g
      const reg = this.format.pattern
      let inputs = [], levels = {}, errors = [], used = {}
      // reset "used" field
      for (let name in this.synonymsRef) {
        used[name] = false
      }
      // check in each line which synonym is used, and errors
      this.model.inputs.split('\n').forEach((input, line) => {
        input = input.replace(/^\s+|\s+$/g, '')
        if (!input) return
        let level = 0, error = null, seen = {}
        input = input.split('\t')
        if (input.length === 1) {
          input = input[0]
        } else if (input.length !== 2) {
          error = '1 or 2 columns expected, got ' + input.length + ' columns'
        } else if (!input[0].match(/^[123]?$/)) {
          error = 'priority should be 1, 2, or 3, got "' + input[0] + '"'
        } else {
          level = parseInt(input[0] || 1) - 1
          input = input[1]
        }
        input = error || input.replace(reg, (_, text, entity, synonym) => {
          if (text) return text
          else if (synonym) {
            if (!(synonym in this.synonymsRef)) {
              this.$set(this.synonymsRef, synonym, {
                name: synonym,
                value: null,
                inconsistent: false,
                disabled: false,
                used: true,
                auto: true,
              })
            }
            // this.synonymsRef[synonym].used = true
            used[synonym] = true
            return '#' + synonym + ':' + (
              this.synonymsRef[synonym].value || 'null')
          } else if (entity) {
            if (seen[entity]) {
              error = error || 'duplicate entity "' + entity + '"'
            } else {
              seen[entity] = true
              return '@' + entity + ':' + (this.entitiesRef[entity] || 'null')
            }
          } else {
            error = 'invalid character'
          }
          return ''
        })
        if (error) {
          errors.push('line ' + (line + 1) + ': ' + error)
        } else {
          if (level) {
            levels[inputs.length + ''] = level
          }
          inputs.push(input)
        }
      })
      // update "used" field
      for (let name in used) {
        let synonym = this.synonymsRef[name]
        if (!used[name] && synonym.auto && !synonym.value) {
          this.$delete(this.synonymsRef, name)
        } else {
          // this.$set(synonym, 'used', used[name])
          synonym.used = used[name]
        }
      }
      this.outputs = {
        inputs: inputs,
        levels: levels,
      }
      this.errors = errors
    },
    /* called when synonym input changed */
    handleSynonym (name, value) {
      let synonym = this.synonymsRef[name]
      synonym.value = value
      synonym.inconsistent = false
    },
    /* closes the editor */
    handleCancel () {
      if (this.inputsText === this.model.inputs) {
        this.$emit('close')
        return
      }
      this.$confirm('This will cancel the inputs edition', 'Cancel', {
        type: 'warning',
      }).then(() => {
        this.$emit('close')
      }).catch(() => {})
    },
    /* resets the editor with inputs */
    handleReset (field, value) {
      if (this.inputsText === this.model.inputs) {
        if (field) this[field] = value
        this.buildModel()
        return
      }
      this.$confirm('This will reset the inputs edition', 'Reset', {
        type: 'warning',
      }).then(() => {
        if (field) this[field] = value
        this.buildModel()
      }).catch(() => {})
    },
    /* save the inputs */
    handleSave () {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.$confirm('This will validate the inputs', 'Validate', {
            type: 'success',
          }).then(() => {
            this.$emit('save', this.outputs)
          }).catch(() => {})
        } else {
          this.$message.error('Please correct errors')
        }
      })
    },
  },
}
</script>

<style lang="scss">
@import "../../element-variables.scss";

.inputs-text-editor {
  .el-form-item__label-wrap {
    margin-left: 0 !important;
  }
  textarea {
    line-height: 150%;
    white-space: pre;
  }
}
</style>
